import { useRef, useEffect, useCallback, useContext } from 'react'
import debounce from 'lodash/debounce'
import { useRouteMatch } from 'react-router-dom'
import { SubmissionsDetailsHeader } from '@components/headers/submissionsDetails'
import { SelectiveDatasetContext } from '@datasets/create-dataset/selective/context'
import { useAppSelector } from '@redux/hooks'
import { useSaveOnToggle } from './hooks/useSaveOnToggle'
import { useTemplate } from '@global/hooks/useTemplate'
import { useSelector } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { getIsMetaIntegration } from '@utils/isMetaIntegration'
/**
 * Submissions Details Header with submission name confirmed via Dialog.
 */
interface SubmissionsHeaderDetailsContainerProps {
  datasetId: string
  templateId: string
}

interface SubmissionsHeaderDetailsContainerRouterParams {
  id?: string
}
export const SubmissionsHeaderDetailsContainer = ({
  datasetId,
  templateId,
}: SubmissionsHeaderDetailsContainerProps) => {
  const isSubmissionPage =
    useRouteMatch<SubmissionsHeaderDetailsContainerRouterParams>('/codes/details/:id')
  const id = isSubmissionPage?.params.id ?? null
  const editableHeaderRef = useRef<HTMLElement | null>(null)
  const {
    state: { submissionName },
    dispatch: dispatchState,
  } = useContext(SelectiveDatasetContext)
  const meta = useAppSelector(state => state.Datasets.datasetMetadataById[id])
  const holdOriginalTitle = useRef(submissionName)
  const { saveSubmissionName } = useSaveOnToggle({ templateId, datasetId, submissionName })

  useEffect(() => {
    if (holdOriginalTitle.current !== 'Untitled Submission') return // ensure it's only the original title
    const $editalbeHeader = document.getElementById('header-submissionname-editable')
    holdOriginalTitle.current = submissionName
    editableHeaderRef.current = $editalbeHeader
  }, [submissionName])

  const { template } = useTemplate({ templateId })
  const [CAMPAIGN_HEALTH_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.CAMPAIGN_HEALTH]),
  )
  const isMetaIntegration = template && getIsMetaIntegration(template)
  const hasCampaignHealth = isMetaIntegration && CAMPAIGN_HEALTH_ENABLED

  useEffect(() => {
    if (!meta?.identifier) return
    dispatchState({ type: 'setSubmissionName', payload: meta.identifier })

    return () => {
      dispatchState({ type: 'setSubmissionName', payload: '' })
    }
  }, [meta?.identifier])

  /**
   * onBlur is cached inside React ContentEditable, so any external values
   * do not change; such as title. The return inside that evaluates title is
   * for the first pass; once the input recieves focus that value will be locked.
   * - The downside to this is that when a user focuses the title it's going to save
   * on each blur; and we cannot compare title and the value to prevent additional saves.
   * - To solve this, we may need to move away from React ContentEditable and do something more custom.
   */
  const onBlurCallback = useCallback(value => {
    dispatchState({ type: 'setUnblockSubmit' })
    saveSubmissionName(value)
  }, [])

  function onFocusCallback() {
    dispatchState({ type: 'setBlockSubmit' })
  }

  const editableTitleCallback = useCallback(
    debounce(function changedTitle(value) {
      saveSubmissionName(value)
      dispatchState({ type: 'setSubmissionName', payload: value })
    }, 250),
    [],
  )

  return (
    <SubmissionsDetailsHeader
      title={submissionName}
      editableTitleCallback={editableTitleCallback}
      onBlurCallback={onBlurCallback}
      onFocusCallback={onFocusCallback}
      templateId={templateId}
      hasCampaignHealth={hasCampaignHealth}
    />
  )
}
